import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: string | Date): string {
  const d = new Date(date);
  return d.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function formatRelativeTime(date: string | Date): string {
  const now = new Date();
  const d = new Date(date);
  const diff = now.getTime() - d.getTime();

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 7) {
    return formatDate(date);
  } else if (days > 0) {
    return `${days} 天前`;
  } else if (hours > 0) {
    return `${hours} 小时前`;
  } else if (minutes > 0) {
    return `${minutes} 分钟前`;
  } else {
    return '刚刚';
  }
}

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return str.slice(0, length) + '...';
}

export function slugify(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Extract external links from markdown content
 * Returns array of { url, text, line }
 */
export function extractExternalLinks(content: string): Array<{ url: string; text: string; line: number }> {
  const links: Array<{ url: string; text: string; line: number }> = [];
  const lines = content.split('\n');

  // Match markdown links: [text](url)
  const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;

  // Match HTML links: <a href="url">text</a>
  const htmlLinkRegex = /<a[^>]+href=["']([^"']+)["'][^>]*>([^<]*)<\/a>/gi;

  // Match bare URLs (http/https)
  const bareUrlRegex = /(?<![\[(])https?:\/\/[^\s<>\[\]()"'`]+/g;

  lines.forEach((line, lineIndex) => {
    // Extract markdown links
    let match;
    while ((match = markdownLinkRegex.exec(line)) !== null) {
      const url = match[2];
      // Check if it's an external link (not wiki link or relative path)
      if (url.startsWith('http://') || url.startsWith('https://')) {
        links.push({
          url,
          text: match[1] || url,
          line: lineIndex + 1,
        });
      }
    }

    // Extract HTML links
    while ((match = htmlLinkRegex.exec(line)) !== null) {
      const url = match[1];
      if (url.startsWith('http://') || url.startsWith('https://')) {
        links.push({
          url,
          text: match[2] || url,
          line: lineIndex + 1,
        });
      }
    }

    // Extract bare URLs (not already in markdown or HTML links)
    while ((match = bareUrlRegex.exec(line)) !== null) {
      const url = match[0];
      // Skip if already captured as markdown link
      const alreadyCaptured = links.some(
        (link) => link.url === url && link.line === lineIndex + 1
      );
      if (!alreadyCaptured) {
        links.push({
          url,
          text: url,
          line: lineIndex + 1,
        });
      }
    }
  });

  // Remove duplicates
  const seen = new Set<string>();
  return links.filter((link) => {
    if (seen.has(link.url)) {
      return false;
    }
    seen.add(link.url);
    return true;
  });
}

/**
 * Check if a URL is external
 */
export function isExternalUrl(url: string): boolean {
  if (!url) return false;
  return url.startsWith('http://') || url.startsWith('https://');
}
